import { Listbox } from '@headlessui/react'
import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { ChevronToggle } from '../ChevronToggle'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { gqlFetchList } from 'app/lib/graphqlClient'

export function ChannelSelector(params: {
  onChannelChange?: (channel: string | null) => void
  buttonClassName?: string
  showLabel?: boolean
  customChannelValueClassName?: string
  isTableFilter?: boolean
  defaultChannel?: string | null
  fetcher?: (params: { query: string }) => Promise<{ hits: string[] }>
}) {
  const user = useUser()
  const defaultChannelByOrg = user && isSMCOrg(user.orgId) ? `Digital Marketing` : null
  const [channel, setChannel] = useState<string | null>(params.defaultChannel ?? defaultChannelByOrg)
  const [searchParams, setSearchParams] = useSearchParams()
  const [channelOptions, setChannelOptions] = useState<{ value: string; display: string }[]>()

  useEffect(() => {
    if (!searchParams.has(`channel`)) {
      setSearchParams((prev) => {
        prev.set(`channel`, channel ?? ``)
        return prev
      })
    }
  }, [])

  useEffect(() => {
    params.onChannelChange?.(channel)
  }, [channel])

  useEffect(() => {
    if (searchParams.has(`channel`)) setChannel(searchParams.get(`channel`))
  }, [searchParams.get(`channel`)])

  useEffect(() => {
    if (!user) return
    let cancelled = false
    async function fetchChannels() {
      const channels = params.fetcher
        ? (await params.fetcher({ query: `` })).hits
        : [
            ...new Set(
              (await gqlFetchList<{ id: string; channel: string }>('channelMappings', {}, 'id channel')).hits
                .map((m) => m.channel)
                .filter(Boolean),
            ),
          ]
      if (!cancelled) {
        setChannelOptions(
          [{ value: ``, display: `All Channels` }].concat(
            channels.map((channel) => ({
              value: channel,
              display: channel,
            })),
          ),
        )
      }
    }

    bg(async () => fetchChannels())
    return () => {
      cancelled = true
    }
  }, [user?.orgId, params.fetcher])

  if (!user) return null

  const onChannelChange = (val) => {
    setChannel(val.value)
    setSearchParams((prev) => {
      prev.set(`channel`, val.value)
      return prev
    })
  }

  return (
    <Listbox value={channel} onChange={onChannelChange}>
      {({ open }) => (
        <div className="relative z-30 bg-surface">
          <Listbox.Button
            className={classNames(`btn btn-tertiary btn-sm h-8 w-52 justify-start text-ui-xs text-default`)}
          >
            {params.showLabel && (
              <>
                <span className="table-filter-options text-ui-xs text-default">Channel</span>&nbsp;
              </>
            )}
            <span className={classNames(params.customChannelValueClassName, `block text-ui-xs text-default`)}>
              {channelOptions?.find((c) => c.value === channel)?.display || channel || `All Channels`}
            </span>
            <span
              className={classNames(
                `pointer-events-none flex items-center`,
                params.isTableFilter ? `pl-2` : `absolute right-0 pr-4`,
              )}
            >
              <ChevronToggle open={open} />
            </span>
          </Listbox.Button>
          <Listbox.Options
            modal={false}
            className="absolute z-50 mt-4 w-52 overflow-auto rounded-lg border border-default bg-surface-raised shadow-elevation-4 ring-0 focus:outline-none"
          >
            {channelOptions?.map((option) => (
              <Listbox.Option
                key={option.value}
                value={option}
                className={classNames(`cursor-pointer px-4 py-2.5 text-body-xs text-strong hover:bg-(--menu-hover)`, {
                  [`filter-option-all`]: option.display.includes(`All`),
                })}
              >
                {option.display}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  )
}
